import React, { useState, useEffect } from "react";
import "./PatientNavigator.css";
import { generateCaregilityURL } from "./utils/aesEncryptor";
import logo2 from "./logo2.png";
import davidchen from "./davidchen.png";
import { useNavigate } from "react-router-dom";

// Firestore
import { db } from "./firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

const PatientNavigator3 = () => {
  const navigate = useNavigate();

  const handleNavigateToReports = () => navigate("/outpatient-schedule");
  const handleNavigateToDashboard = () => navigate("/patient-dashboard3");
  const handleNavigateToMonitor = () => navigate("/video-wall");
  const handleNavigateToCensus = () => navigate("/patient-list");

  // States
  const [deviceId, setDeviceId] = useState("66184");
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [vLertEndTime, setVLertEndTime] = useState(null);
  const [MEWSEndTime, setMEWSEndTime] = useState(null);
  const [ConnectTime, setConnectTime] = useState(null);

  const [activeTab, setActiveTab] = useState("Navigator");
  const [notes, setNotes] = useState([]);

  const secretKey = "9tH0WsxD1Ko";
  const patientMRN = "99001122";

  // 🔄 Restore vLert timestamp on navigation (but NOT on refresh)
  useEffect(() => {
    if (window.vLertTimestamps && window.vLertTimestamps[deviceId]) {
      setVLertEndTime(window.vLertTimestamps[deviceId]);
    }
  }, [deviceId]);

  // 🔄 Live-listen for SOAP notes
  useEffect(() => {
    const notesRef = collection(db, "patients", patientMRN, "notes");
    const q = query(notesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const noteData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotes(noteData);
    });

    return () => unsubscribe();
  }, [patientMRN]);

  // 📹 Launch room camera
  const handleRoomCameraClick = () => {
    if (!deviceId) {
      alert("No device ID provided.");
      return;
    }
    const url = generateCaregilityURL(deviceId, secretKey);
    window.open(url, "_blank");
  };

  // 🚨 End vLert — reset on refresh, persist across navigation
  const handleEndVLert = () => {
    const timestamp = new Date().toLocaleString();

    // in-memory only (clears on browser refresh)
    if (!window.vLertTimestamps) window.vLertTimestamps = {};
    window.vLertTimestamps[deviceId] = timestamp;

    // update UI
    setVLertEndTime(timestamp);

    // clear the alert icon in PatientSelector
    if (!window.clearedAlerts) window.clearedAlerts = {};
    window.clearedAlerts[deviceId] = true;

    console.log("✔ Demo vLert cleared for:", deviceId);
  };

  // 🚨 End MEWS — same behavior (timestamp only)
  const handleEndMEWS = () => {
    const timestamp = new Date().toLocaleString();
    setMEWSEndTime(timestamp);

    if (!window.mewsTimestamps) window.mewsTimestamps = {};
    window.mewsTimestamps[deviceId] = timestamp;
  };

  // 🚨 Connect — same behavior (timestamp only)
    const handleConnect = () => {
      const timestamp = new Date().toLocaleString();
      setConnectTime(timestamp);

      if (!window.connectTimestamps) window.connectTimestamps = {};
      window.connectTimestamps[deviceId] = timestamp;
    };

  // Save SOAP Note
  const saveSOAPNote = async (soap) => {
    await addDoc(collection(db, "patients", patientMRN, "notes"), {
      createdAt: serverTimestamp(),
      author: "Virtual RN",
      soap,
    });
  };

  // Demo SOAP Note
  const handleSimulateSOAP = () => {
    saveSOAPNote({
      subjective: "Patient reports fever, chills, painful urination",
      objective: "RR 26, BP 86/55, Tempp 101.8",
      assessment: "Sepsis secondary to UTI.",
      plan: "Initiate Sepsis Protocol. 30cc/kg NS IV. Lactic Acid. Blood Cultures. Antibiotics.",
    });
  };

  return (
    <div className="nav-page-container">
      <div className="nav-header">
        <img src={logo2} alt="Logo" className="nav-logo" />
        <h1>Patient Chart</h1>
      </div>

      {/* TOP RIGHT NAV */}
      <div className="top-nav-buttons">
        <button onClick={handleNavigateToDashboard} className="top-nav-btn">Patient Dashboard</button>
        <button onClick={handleNavigateToMonitor} className="top-nav-btn">Monitor Rooms</button>
        <button onClick={handleNavigateToReports} className="top-nav-btn">Outpatient Schedule</button>
      </div>

      {/* TOP TABS */}
      <div className="nav-tabs2">
        <div className={`camera-btn ${activeTab === "Census" ? "active" : ""}`}
          onClick={handleNavigateToCensus}>
          Patient Census
        </div>
        <div className={`nav-tab ${activeTab === "Vitals" ? "active" : ""}`}
          onClick={() => setActiveTab("Vitals")}>
          Vital Signs
        </div>
        <div className={`nav-tab ${activeTab === "Notes" ? "active" : ""}`}
          onClick={() => setActiveTab("Notes")}>
          Notes
        </div>
        <div className={`nav-tab ${activeTab === "Labs" ? "active" : ""}`}
          onClick={() => setActiveTab("Labs")}>
          Labs
        </div>
        <div className={`nav-tab ${activeTab === "LDAs" ? "active" : ""}`}
          onClick={() => setActiveTab("LDAs")}>
          LDAs
        </div>
        <div className={`nav-tab ${activeTab === "Navigator" ? "active" : ""}`}
          onClick={() => setActiveTab("Navigator")}>
          Navigator
        </div>
      </div>

      <div className="nav-layout">
        {/* LEFT PANEL */}
        <div className="nav-patient-info">
          <img
            src={davidchen}
            alt="Patient"
            className="patient-avatar"
          />
          <h2>David Chen</h2>
          <p><strong>Male, 81 y.o. 5/12/1944</strong></p>
          <p><strong>MRN:</strong> {patientMRN}</p>
          <p><strong>Code:</strong> FULL, <strong>Room:</strong> 145</p>
          <p><strong>Diagnosis:</strong> Sepsis - UTI</p>
        </div>

        {/* MAIN CONTENT */}
        <div className="nav-content">
          {activeTab === "Navigator" && (
            <>
              {/* CAMERA PANEL */}
              <div className="nav-card camera-card">
                <div className="camera-header">
                  <h2>Camera</h2>
                  <button
                    className="settings-btn"
                    onClick={() => setShowDeviceSettings(!showDeviceSettings)}
                    title="Show/Hide Camera Settings"
                  >
                    <i className="fas fa-wrench"></i>
                  </button>
                </div>

                {showDeviceSettings && (
                  <div className="device-settings">
                    <label>Device ID</label>
                    <input
                      value={deviceId}
                      onChange={(e) => setDeviceId(e.target.value)}
                    />
                  </div>
                )}

                <div className="camera-actions">
                  <button className="camera-btn" onClick={() => {handleRoomCameraClick(); handleConnect()}}>
                    Room Camera
                  </button>
                  <button className="end-vlert-btn" onClick={handleEndVLert}>
                    End vLert
                  </button>
                </div>

                {vLertEndTime && (
                  <p className="vlert-timestamp">vLert Ended at {vLertEndTime}</p>
                )}

                {ConnectTime && (
                  <p className="vlert-timestamp">Virtual Nurse Connected at {ConnectTime}</p>
                )}
              </div>  

              {/* MEWS PANEL */}
              <div className="nav-card">
                <h2>MEWS Alert</h2>
                <p><strong>MEWS:</strong> 5</p>
                <button className="end-vlert-btn" onClick={handleEndMEWS}>
                  End Red MEWS Alert
                </button>
                {MEWSEndTime && (
                  <p className="vlert-timestamp">
                    MEWS Alert Ended at {MEWSEndTime}
                  </p>
                )}
              </div>

              {/* HISTORY */}
              <div className="nav-card history-card">
                <h2>History</h2>
                <div className="nav-item">
                  <strong>Diabetes Mellitus</strong>
                  <p>Metformin 500mg BID</p>
                  <p>Sliding Scale Insulin PRN</p>
                </div>
                <div className="nav-item">
                  <strong>Hypertension, CKD</strong>
                  <p>Lisinopril 20mg Daily</p>
                </div>
              </div>

              {/* IMMUNIZATIONS */}
              <div className="nav-card">
                <h2>Immunizations</h2>
                <p>Moderna SARS-CoV-2 Vaccination</p>
              </div>
            </>
          )}

          {/* NOTES TAB */}
          {activeTab === "Notes" && (
            <div className="nav-card">
              <h2>Progress Notes</h2>

              {notes.length === 0 && <p>No notes yet.</p>}

              {notes.map((note) => (
                <div key={note.id} className="note-entry">
                  <p className="note-timestamp">
                    {(note.createdAt?.toDate
                      ? note.createdAt.toDate()
                      : new Date()
                    ).toLocaleString()}
                  </p>

                  {note.soap ? (
                    <>
                      <p><strong>S:</strong> {note.soap.subjective || "—"}</p>
                      <p><strong>O:</strong> {note.soap.objective || "—"}</p>
                      <p><strong>A:</strong> {note.soap.assessment || "—"}</p>
                      <p><strong>P:</strong> {note.soap.plan || "—"}</p>
                    </>
                  ) : (
                    <p>{note.rawFHIR?.text?.div || "No content"}</p>
                  )}

                  <hr />
                </div>
              ))}

              <button className="camera-btn" onClick={handleSimulateSOAP}>
                Simulate SOAP Note
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientNavigator3;
